import * as IF from "infrontjs";
import template from "./form.html";
import { validateScope, resetValidation, bindLiveClear } from "../general/form-validation";
import { _lcs } from "../../i18n";

// Platform-defined regions (core-api: system/model/Region.js). The labels below
// are German source strings and double as dictionary keys — these constants are
// evaluated before I18n exists, so they are translated where they are read.
const REGIONS = [
    { value: "saechsische_schweiz", label: "Sächsische Schweiz" }
];

// What a source delivers (core-api: EventSource.CONTENT_TYPE). Shown, not
// edited: it selects the importer's target table, which is code.
const CONTENT_TYPE_LABELS = {
    event  : "Veranstaltungen",
    poi    : "Sehenswürdigkeiten",
    gastro : "Gastronomie",
    tour   : "Touren"
};

/**
 * <event-source-form> — modal to edit one external event feed.
 *
 * Edit only: sources are seeded by a patch when their adapter ships, because a
 * feed without an importer is just a row. What is configurable here is the
 * connection (endpoint, experience, licence key) and the import window.
 *
 * The licence key is write-only. The API returns it masked, so the input opens
 * empty with the mask as its placeholder: leaving it alone keeps the stored
 * secret, typing replaces it. Sending the mask back would overwrite a working
 * key with bullet characters.
 *
 * On a successful save it dispatches a bubbling "event-source-saved" event so
 * the host state can refresh its list.
 */
class EventSourceForm extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
        this._id = null;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );

        this._modal = this.querySelector( '[data-id="evs-modal"]' );
        this._form = this.querySelector( '[data-id="evs-form-el"]' );
        this._title = this.querySelector( '[data-id="evs-title"]' );
        this._key = this.querySelector( '[data-id="evs-key"]' );
        this._error = this.querySelector( '[data-id="evs-error"]' );
        this._submit = this.querySelector( '[data-id="evs-submit"]' );
        this._licenseKey = this.querySelector( "#evs-licensekey" );

        this._populateRegions();
    }

    bind()
    {
        this.querySelector( '[data-id="evs-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="evs-cancel"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="evs-backdrop"]' ).addEventListener( "click", () => this.close() );
        this._form.addEventListener( "submit", ( e ) => this.onSubmit( e ) );

        bindLiveClear( this._form );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
    }

    /* ------------------------------- Public API ------------------------------ */

    openEdit( source )
    {
        this._id = source.id;
        const config = source.config || {};

        this._clearError();
        this._form.reset();
        resetValidation( this._form );

        this._title.textContent = _lcs( "Datenquelle bearbeiten" );
        const contentType = source.contentType || "event";
        this._key.textContent = source.key +
            " · " + String( source.adapter || "" ).toUpperCase() +
            " · " + ( CONTENT_TYPE_LABELS[ contentType ] ? _lcs( CONTENT_TYPE_LABELS[ contentType ] ) : contentType );

        this._val( "evs-name", source.name || "" );
        this._val( "evs-description", source.description || "" );
        this._selectValue( "evs-region", source.region || REGIONS[ 0 ].value );
        this._field( "evs-enabled" ).checked = source.isEnabled === true;

        this._val( "evs-endpoint", config.endpoint || "" );
        this._val( "evs-experience", config.experience || "" );

        // Masked, never prefilled — see the class comment.
        this._licenseKey.value = "";
        this._licenseKey.placeholder = config.licenseKey || _lcs( "noch nicht hinterlegt" );

        this._val( "evs-template", config.template || "" );
        this._val( "evs-type", config.type || "" );
        this._val( "evs-months", config.months != null ? config.months : "" );
        this._val( "evs-maxdistance", config.maxDistanceKm != null ? config.maxDistanceKm : "" );
        this._val( "evs-pagesize", config.pageSize != null ? config.pageSize : "" );
        this._val( "evs-maxpages", config.maxPages != null ? config.maxPages : "" );
        this._field( "evs-autopublish" ).checked = config.autoPublish !== false;
        this._field( "evs-detectduplicates" ).checked = config.detectDuplicates !== false;
        this._field( "evs-mirrorimages" ).checked = config.mirrorImages !== false;
        this._val( "evs-imagemaxpixels", config.imageMaxPixels != null ? config.imageMaxPixels : "" );

        this._modal.hidden = false;
        this._field( "evs-name" ).focus();
    }

    close()
    {
        this._modal.hidden = true;
        this._clearError();
    }

    /* -------------------------------- Submit -------------------------------- */

    async onSubmit( e )
    {
        e.preventDefault();
        this._clearError();

        if ( !validateScope( this._form ) )
        {
            return;
        }

        const config = {
            endpoint    : this._val( "evs-endpoint" ).trim(),
            experience  : this._val( "evs-experience" ).trim(),
            template    : this._val( "evs-template" ).trim(),
            type        : this._val( "evs-type" ).trim(),
            months      : this._number( "evs-months" ),
            pageSize    : this._number( "evs-pagesize" ),
            maxPages    : this._number( "evs-maxpages" ),
            // Empty means "no limit", which is a real value here, not a gap.
            maxDistanceKm : this._number( "evs-maxdistance" ),
            autoPublish : this._field( "evs-autopublish" ).checked,
            detectDuplicates : this._field( "evs-detectduplicates" ).checked,
            mirrorImages : this._field( "evs-mirrorimages" ).checked,
            imageMaxPixels : this._number( "evs-imagemaxpixels" )
        };

        // Only send a licence key the operator actually typed.
        const typedKey = this._licenseKey.value.trim();
        if ( typedKey.length > 0 )
        {
            config.licenseKey = typedKey;
        }

        // Numeric fields left empty stay as they are, except the radius, where
        // empty is the "off" setting.
        Object.keys( config ).forEach( ( key ) =>
        {
            if ( config[ key ] === null && key !== "maxDistanceKm" )
            {
                delete config[ key ];
            }
        } );

        const payload = {
            name        : this._val( "evs-name" ).trim(),
            description : this._val( "evs-description" ).trim(),
            region      : this._val( "evs-region" ),
            isEnabled   : this._field( "evs-enabled" ).checked,
            config      : config
        };

        this._setSubmitting( true );
        try
        {
            const { result } = await this.app.api.patch( "/eventsource/" + this._id, payload );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected update response." );
            }

            this.app.view.toast( _lcs( "„{name}“ wurde gespeichert.", { name: payload.name } ), "success" );
            this.close();
            this.dispatchEvent( new CustomEvent( "event-source-saved", {
                bubbles: true,
                detail: { source: result.data || null }
            } ) );
        }
        catch ( err )
        {
            this._showError( this._errorForStatus( +err?.status ) );
        }
        finally
        {
            this._setSubmitting( false );
        }
    }

    /* -------------------------------- Helpers -------------------------------- */

    _populateRegions()
    {
        const select = this._field( "evs-region" );
        select.innerHTML = "";
        REGIONS.forEach( ( region ) =>
        {
            const option = document.createElement( "option" );
            option.value = region.value;
            option.textContent = _lcs( region.label );
            select.appendChild( option );
        } );
    }

    _selectValue( id, value )
    {
        const select = this._field( id );
        if ( value && !Array.from( select.options ).some( ( o ) => o.value === value ) )
        {
            const option = document.createElement( "option" );
            option.value = value;
            option.textContent = value;
            select.appendChild( option );
        }
        select.value = value || "";
    }

    _field( id )
    {
        return this.querySelector( "#" + id );
    }

    _val( id, value )
    {
        const el = this._field( id );
        if ( value !== undefined )
        {
            if ( el ) el.value = value;
            return value;
        }
        return el ? el.value : "";
    }

    _number( id )
    {
        const raw = this._val( id );
        if ( raw === "" || raw === null || raw === undefined )
        {
            return null;
        }
        const parsed = Number( raw );
        return Number.isFinite( parsed ) ? parsed : null;
    }

    _errorForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Bitte überprüfe deine Eingaben." );
            case 401:
            case 403: return _lcs( "Keine Berechtigung für diese Aktion." );
            case 404: return _lcs( "Die Quelle wurde nicht gefunden." );
            default:  return _lcs( "Speichern fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    _setSubmitting( on )
    {
        this._submit.disabled = on;
        this._submit.setAttribute( "aria-busy", String( on ) );
    }

    _showError( message )
    {
        this._error.textContent = message;
        this._error.hidden = false;
    }

    _clearError()
    {
        this._error.textContent = "";
        this._error.hidden = true;
    }
}

customElements.define( "event-source-form", EventSourceForm );
